import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import NotificationCard from './components/NotificationCard';
import {
  fetchNotifications,
  markAllAsRead,
  getUnreadCount,
  subscribeToNotifications,
  unsubscribeFromNotifications
} from '../../services/notificationService';

const NotificationCenter = () => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();

    // Subscribe to real-time updates
    const channel = subscribeToNotifications((payload) => {
      handleRealtimeUpdate(payload);
    });

    return () => {
      unsubscribeFromNotifications(channel);
    };
  }, [filterType, filterRead]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const filters = {
        type: filterType,
        isRead: filterRead === 'all' ? undefined : filterRead === 'read'
      };

      const { data, error: fetchError } = await fetchNotifications(filters);
      if (fetchError) {
        setError(fetchError);
      } else {
        setNotifications(data || []);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    const { count } = await getUnreadCount();
    setUnreadCount(count);
  };

  const handleRealtimeUpdate = (payload) => {
    console.log('Real-time notification update:', payload);
    
    // Reload notifications and unread count
    if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
      loadNotifications();
      loadUnreadCount();
    }
  };

  const handleMarkAllAsRead = async () => {
    const { error: markError } = await markAllAsRead();
    if (!markError) {
      loadNotifications();
      loadUnreadCount();
    }
  };

  const handleNotificationUpdate = () => {
    loadNotifications();
    loadUnreadCount();
  };

  const notificationTypes = [
    { value: 'all', label: 'All Notifications' },
    { value: 'ai_match', label: 'AI Matches' },
    { value: 'deal_progress', label: 'Deal Progress' },
    { value: 'market_value', label: 'Market Value Changes' },
    { value: 'scout_response', label: 'Scout Responses' },
    { value: 'transfer_inquiry', label: 'Transfer Inquiries' },
    { value: 'profile_view', label: 'Profile Views' },
    { value: 'club_interest', label: 'Club Interest' }
  ];

  const readStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'unread', label: 'Unread Only' },
    { value: 'read', label: 'Read Only' }
  ];

  if (loading && notifications?.length === 0) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading notifications...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Bell" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground font-headline">
                    Notification Center
                  </h1>
                  {unreadCount > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                iconName="CheckCheck"
                iconPosition="left"
              >
                Mark All as Read
              </Button>
            )}
          </div>

          {/* Filters Section */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Notification Type
                </label>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                  options={notificationTypes}
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Read Status
                </label>
                <Select
                  value={filterRead}
                  onChange={(e) => setFilterRead(e?.target?.value)}
                  options={readStatusOptions}
                />
              </div>
            </div>
          </div>

          {/* Live Indicator */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs font-medium text-success">Live Updates Active</span>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {/* Notifications List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading notifications...</p>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Bell" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No Notifications
              </h3>
              <p className="text-sm text-muted-foreground">
                {filterType !== 'all' || filterRead !== 'all' ?'No notifications match your filters. Try adjusting your filter settings.' :'You\'re all caught up! New notifications will appear here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  onUpdate={handleNotificationUpdate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;